import type { AlertGroupState, AlertGroupStateDetails, AlertStateChange } from './generated/schemas';
import { AlertGroupStateValue, AlertState } from './generated/schemas';

export type AlertStateChangeDetails = Partial<Omit<AlertGroupStateDetails, '__typename'>>;

export type AlertStateChangeWithDetails = AlertStateChange & {
  details?: AlertStateChangeDetails | null;
};

/**
 * The subset of AlertGroupState fields the alert-state UI reads. The generated query
 * result types only select a few fields, so we accept the minimal shape rather than the
 * full row.
 */
type GroupStateLike = {
  created_at: AlertGroupState['created_at'];
  current_state: AlertGroupState['current_state'];
  group_by_key: AlertGroupState['group_by_key'];
  investigation_conversation_id?: AlertGroupState['investigation_conversation_id'];
  details?: AlertStateChangeDetails | null;
};

/**
 * Map a Postgres alert-group-state value to the legacy ClickHouse AlertState so the
 * existing alert-state UI renders group-state data unchanged during the resolver
 * migration.
 *
 * NoData maps to Normal for now — surfacing no-data as its own status (and the
 * high-cardinality treatment) is a follow-up. Mapping it to Normal keeps a no-data group
 * from reading as "firing".
 */
export function alertGroupStateValueToAlertState(value: AlertGroupStateValue): AlertState {
  switch (value) {
    case AlertGroupStateValue.Alert:
      return AlertState.Alerting;
    case AlertGroupStateValue.Warn:
      return AlertState.Warning;
    case AlertGroupStateValue.Error:
      return AlertState.Error;
    case AlertGroupStateValue.NoData:
    case AlertGroupStateValue.Normal:
    default:
      return AlertState.Normal;
  }
}

/** Adapt a single Postgres group-state row to the legacy AlertStateChange shape. */
export function groupStateToStateChange(state: GroupStateLike): AlertStateChangeWithDetails {
  const change = {
    timestamp: state.created_at,
    state: alertGroupStateValueToAlertState(state.current_state),
    groupByKey: state.group_by_key,
    investigationConversationId: state.investigation_conversation_id ?? null,
  } as AlertStateChangeWithDetails;
  if (state.details) {
    change.details = state.details;
  }
  return change;
}

/** Adapt a list of Postgres group-state rows, dropping nulls, to legacy AlertStateChanges. */
export function groupStatesToStateChanges(
  states: ReadonlyArray<GroupStateLike | null | undefined> | null | undefined,
): AlertStateChangeWithDetails[] {
  return (states ?? []).filter((state): state is GroupStateLike => state != null).map(groupStateToStateChange);
}
